import { useMemo } from 'react'

import { ChertoExperiments } from 'constants/experiments'
import { ANYWHERE_PLACE_ID } from 'constants/search'
import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import useExperiment from 'hooks/Experiments/useExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { usePlaceById } from 'hooks/usePlace'
import { isMaldivesDestinationCountryPending, isMaldivesDestinationSearch } from 'lib/offer/isMaldivesHotel'
import { isAnywhereDestinationSearch } from 'lib/search/searchUtils'
import { isPaidSearch } from 'marketing/lib/lib'
import { isCurrentDeviceMobile } from 'selectors/configSelectors'

interface CroMobileCompactSearchTileEligibility {
  readonly headlessMode?: boolean
  readonly isMobile: boolean
  readonly pathname: string
  readonly utmBp?: string
  /** Maldives country or island/atoll destination (commercial exclusion). */
  readonly isMaldivesDestination?: boolean
  /**
   * Place lookup still unresolved for a destination that might be a Maldives island/atoll.
   * Known catalog destinations and already-classified Maldives searches must not wait.
   * Failed, missing, and unresolved comma-separated lookups stay pending so unclassified
   * atolls cannot fail open.
   */
  readonly isMaldivesDestinationPending?: boolean
  /** Anywhere or missing-destination (worldwide) searches are commercially excluded. */
  readonly isAnywhereSearch?: boolean
}

export interface CroMobileCompactSearchTileExperimentValue {
  readonly canActivate: boolean
  readonly density: SearchTileDensity
  readonly isCroCompact: boolean
  /**
   * Paid mobile `/search` and `/search/homes-and-villas` traffic (`utm_bp=2`), including CRO control, destination
   * pending, and confirmed Maldives. Use this to keep Optimizely denser tiles off
   * so control stays a single baseline and Maldives atolls cannot flash default → compact
   * after place lookup. CRO mosaic activation still excludes Maldives separately.
   */
  readonly isCroAudience: boolean
}

const GENERAL_SEARCH_SEGMENT = 'search'
const EXCLUDED_GENERAL_SEARCH_PATH_SEGMENTS = [
  ['metasearch'],
  ['search-fullscreen'],
  ['search', 'experiences'],
  ['search', 'cruises'],
  ['search', 'tours'],
  ['search', 'car-hire'],
  ['search', 'shop'],
  // Map surfaces keep the default tile treatment (CRO-6924 out of scope).
  ['search', 'map-split'],
  ['search', 'store', 'map'],
  ['search', 'map'],
  ['search', 'homes-and-villas', 'map'],
] as const

function getPathSegments(pathname: string): Array<string> {
  return pathname.split('/').filter(Boolean)
}

function hasPathSegmentSequence(pathSegments: ReadonlyArray<string>, expectedSegments: ReadonlyArray<string>): boolean {
  return pathSegments.some((_, startIndex) =>
    expectedSegments.every((expectedSegment, segmentIndex) => pathSegments[startIndex + segmentIndex] === expectedSegment),
  )
}

export function isCroMobileCompactGeneralSearchPath(pathname: string): boolean {
  const pathSegments = getPathSegments(pathname)
  const isExcludedPath = EXCLUDED_GENERAL_SEARCH_PATH_SEGMENTS.some(excludedPathSegments =>
    hasPathSegmentSequence(pathSegments, excludedPathSegments),
  )
  if (isExcludedPath) return false

  if (pathSegments[pathSegments.length - 1] === GENERAL_SEARCH_SEGMENT) return true

  return pathSegments[pathSegments.length - 1] === 'homes-and-villas' &&
    pathSegments[pathSegments.length - 2] === GENERAL_SEARCH_SEGMENT
}

/** Anywhere place id, or no destination selected (worldwide search). */
export function isCroMobileCompactAnywhereSearch(destinationId?: string): boolean {
  return !destinationId || isAnywhereDestinationSearch(destinationId)
}

export function isCroMobileCompactSearchTileAudience({
  headlessMode = false,
  isMobile,
  pathname,
  utmBp,
}: Pick<CroMobileCompactSearchTileEligibility, 'headlessMode' | 'isMobile' | 'pathname' | 'utmBp'>): boolean {
  return !headlessMode &&
    isMobile &&
    isPaidSearch(utmBp) &&
    isCroMobileCompactGeneralSearchPath(pathname)
}

// Reuse the same audience for experiments that must not overlap with CRO-6924.
// This deliberately includes its control bucket and destination exclusions.
export function useCroMobileCompactSearchTileAudience(): boolean {
  return useAppSelector(state => isCroMobileCompactSearchTileAudience({
    headlessMode: state.system.headlessMode,
    isMobile: isCurrentDeviceMobile(state),
    pathname: state.router.location.pathname,
    utmBp: state.utm.bp,
  }))
}

export function canActivateCroMobileCompactSearchTileExperiment({
  headlessMode = false,
  isMobile,
  pathname,
  utmBp,
  isMaldivesDestination = false,
  isMaldivesDestinationPending = false,
  isAnywhereSearch = false,
}: CroMobileCompactSearchTileEligibility): boolean {
  // Google Search Ads only (utm_bp=2). Exclude GHA / metasearch (utm_bp=3 + /metasearch)
  // and native app webviews (headlessMode).
  // Maldives, Anywhere, and worldwide (no destination) searches are commercially
  // excluded — no control/on activation or Snowplow exposure.
  return isCroMobileCompactSearchTileAudience({
    headlessMode,
    isMobile,
    pathname,
    utmBp,
  }) && !isMaldivesDestination && !isMaldivesDestinationPending && !isAnywhereSearch
}

/**
 * CRO-6924 horizontal mosaic search tiles (Cherto `cro-mobile-compact-search-tile`).
 * Independent from Optimizely `comms_compact_mobile_search_tile`.
 */
function useCroMobileCompactSearchTileExperiment(
  filters?: Pick<App.OfferListFilters, 'destinationId' | 'destinationName'>,
): CroMobileCompactSearchTileExperimentValue {
  const pathname = useAppSelector(state => state.router.location.pathname)
  const utmBp = useAppSelector(state => state.utm.bp)
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  // UA-based mobile check is SSR-stable (avoids carousel→mosaic flash after hydrate).
  const isMobile = useIsMobileDevice()

  const destinationId = filters?.destinationId
  const destinationName = filters?.destinationName
  const shouldSkipPlaceLookup = !destinationId || destinationId.includes(',') || destinationId === ANYWHERE_PLACE_ID
  const destinationPlace = usePlaceById(destinationId, shouldSkipPlaceLookup)
  const isAnywhereSearch = isCroMobileCompactAnywhereSearch(destinationId)
  const isMaldivesDestinationPending = isMaldivesDestinationCountryPending(
    destinationId,
    destinationName,
    destinationPlace,
    shouldSkipPlaceLookup,
  )
  const isMaldivesDestination = isMaldivesDestinationSearch(
    destinationId,
    destinationName,
    destinationPlace?.countryCode,
  )

  const isCroAudience = useCroMobileCompactSearchTileAudience()
  const canActivate = useMemo(
    () => canActivateCroMobileCompactSearchTileExperiment({
      headlessMode,
      isMobile,
      pathname,
      utmBp,
      isMaldivesDestination,
      isMaldivesDestinationPending,
      isAnywhereSearch,
    }),
    [headlessMode, isAnywhereSearch, isMaldivesDestination, isMaldivesDestinationPending, isMobile, pathname, utmBp],
  )

  const { isAnyVariant } = useExperiment(
    ChertoExperiments.croMobileCompactSearchTile,
    { canActivate },
  )

  const density: SearchTileDensity = isAnyVariant && canActivate ? 'cro-compact' : 'default'

  return useMemo(
    () => ({
      canActivate,
      density,
      isCroCompact: density === 'cro-compact',
      isCroAudience,
    }),
    [canActivate, density, isCroAudience],
  )
}

export default useCroMobileCompactSearchTileExperiment
